import { FetchResult } from "@apollo/client";
import { HbLoadingSpinner, useModal } from "@homebound/beam";
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { CommitmentStatus, TaskType } from "src/generated/graphqlTypes";
import { useTaskBillModalContextLazyQuery } from "src/routes/projects/schedule-v2/contexts/TaskBillModalContext.generated";
import { SaveScheduleTasksMutation } from "src/routes/projects/schedule-v2/ProjectSchedulePageV2.generated";
import { fail, isDefined } from "src/utils";
import { PlanTaskBillModal } from "../../dynamic-schedules/components/bill-modal/PlanTaskBillModal";
import { TaskBillModal, TaskBillModalProps } from "../detail-pane/taskBills/TaskBillModal";

type TaskBillModalContextType = {
  // Function to open taskBill modal with the needed modal opts
  openTaskBillModal: (modalOpts: TaskBillModalProps) => void;
  // The `handleSaveTask` mutation result object to be accessed by consuming component that opened the modal
  contextSavedTaskResult: FetchResult<SaveScheduleTasksMutation>;
};

export const TaskBillContext = createContext<TaskBillModalContextType>({
  openTaskBillModal() {},
  contextSavedTaskResult: {},
});

// We create a C2P taskBill modal context in order to provide the ability to
// 1. Open the taskBill modal from any component
// 2. Take a `handleSaveTask` function that can only be called _after_ the taskBill modal form is submitted correctly
// 3. Provide the `handleSaveTask` mutation result object to the component that opened the modal
export function TaskBillModalProvider({ children }: { children: React.ReactNode }) {
  const { openModal } = useModal();
  const [loadTask] = useTaskBillModalContextLazyQuery();
  const [contextSavedTaskResult, setContextTaskResult] = useState<FetchResult<SaveScheduleTasksMutation>>({});

  const openTaskBillModal = useCallback(
    async (modalOpts: TaskBillModalProps) => {
      const { taskId, tradePartnerId, handleTaskSave, skipModal, onComplete } = modalOpts;
      if (!taskId) fail("No task id provided");

      // query and load the task to make sure we have all the required data before opening the modal
      const { loading, data } = await loadTask({ variables: { taskId } });
      if (data) {
        const task = data?.task ?? fail("No task found");
        const { project, protoBills } = task;

        const savedTaskHasUnbilledItems = (protoBills ?? [])
          .flatMap((protoBill) => protoBill.lineItems)
          .filter(
            ({ commitmentLineItem: cli }) =>
              cli.owner.status === CommitmentStatus.Signed && cli.owner.tradePartner?.enableClickToPay,
          ).nonEmpty;

        // If the task does not have unbilled items, we don't need to open the modal
        // And we can just call the handleTaskSave function and set the result in the context
        if (!savedTaskHasUnbilledItems || skipModal) {
          const saveResult = handleTaskSave && (await handleTaskSave());
          saveResult && setContextTaskResult(saveResult);
          onComplete && (await onComplete());
          return;
        }

        if (savedTaskHasUnbilledItems && isDefined(project)) {
          openModal({
            content:
              task.type.code === TaskType.PlanTask ? (
                <PlanTaskBillModal taskId={task.id} tradePartnerId={tradePartnerId} onComplete={onComplete} />
              ) : (
                <TaskBillModal
                  taskId={task.id}
                  tradePartnerId={tradePartnerId}
                  handleTaskSave={handleTaskSave}
                  setContextTaskResult={setContextTaskResult}
                  onComplete={onComplete}
                />
              ),
          });
        }
      } else if (loading) {
        openModal({ content: <HbLoadingSpinner /> });
      }
    },
    [loadTask, openModal],
  );

  const savedTaskRes = useMemo(() => contextSavedTaskResult, [contextSavedTaskResult]);

  return (
    // Provider gives access to the `openTaskBillModal` function and
    // The saved task result to be accessed by any child component
    <TaskBillContext.Provider value={{ contextSavedTaskResult: savedTaskRes, openTaskBillModal }}>
      {children}
    </TaskBillContext.Provider>
  );
}

export const useTaskBillContext = () => useContext(TaskBillContext);
